import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, Download, Loader2, AlertTriangle, Wrench, Check, X, Eraser } from "lucide-react";
import { publicApi, publicFileUrl, formatPublicErr } from "../../lib/publicApi";

const fmtCAD = (v) => new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(Number(v || 0));
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-CA", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};

export default function PublicEstimateView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [showApprove, setShowApprove] = useState(null); // null | {} | {optionId}
  const [showDecline, setShowDecline] = useState(false);

  const load = () => publicApi.get(`/estimate/${token}`).then(({ data }) => setData(data));

  useEffect(() => {
    load().catch((e) => setErr(formatPublicErr(e))).finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 size={28} className="animate-spin text-slate-400"/></div>;
  }
  if (err || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-6 text-center">
        <AlertTriangle size={36} className="text-rose-500 mb-3"/>
        <h1 className="font-bold text-xl text-slate-900 mb-2">Estimate unavailable</h1>
        <p className="text-sm text-slate-600 max-w-md">{err || "This link is invalid or has been revoked."}</p>
      </div>
    );
  }

  const est = data.estimate;
  const customer = data.customer;
  const settings = data.settings;
  const primary = settings.primary_color || "#0F4C81";
  const decided = est.status === "Approved" || est.status === "Declined";

  return (
    <div data-testid="public-estimate-view" className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {settings.has_logo ? (
              <img src={publicFileUrl(`/estimate/${token}/logo`)} alt="Logo" className="h-10 w-auto max-w-[120px] object-contain"/>
            ) : (
              <div className="w-10 h-10 rounded flex items-center justify-center shrink-0" style={{ background: primary }}>
                <Wrench className="text-white" size={20}/>
              </div>
            )}
            <div className="min-w-0">
              <div className="font-display font-bold text-slate-900 text-sm sm:text-base truncate">{settings.business_name || "Your provider"}</div>
              {settings.address && <div className="text-[11px] sm:text-xs text-slate-500 truncate">{settings.address}</div>}
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <button onClick={() => window.print()} className="px-3 py-2 text-xs sm:text-sm border border-slate-300 rounded hover:bg-slate-50 flex items-center gap-1.5">
              <Printer size={14}/> <span className="hidden sm:inline">Print</span>
            </button>
            <a href={publicFileUrl(`/estimate/${token}/pdf`)} target="_blank" rel="noopener noreferrer" className="px-3 py-2 text-xs sm:text-sm rounded text-white flex items-center gap-1.5" style={{ background: primary }}>
              <Download size={14}/> <span className="hidden sm:inline">PDF</span>
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="bg-white border border-slate-200 rounded-md sm:rounded-lg shadow-sm overflow-hidden">
          <div className="px-5 sm:px-8 py-6 sm:py-8 border-b border-slate-100 flex justify-between items-start gap-4 flex-wrap">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">Estimate</div>
              <h1 className="font-display text-2xl sm:text-3xl font-bold tabular text-slate-900 mt-1">{est.number}</h1>
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700">{est.status}</span>
                {est.expiration_date && <span className="text-xs text-slate-500">Valid until {fmtDate(est.expiration_date)}</span>}
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">Total</div>
              <div className="font-display text-3xl sm:text-4xl font-bold tabular mt-1" style={{ color: primary }}>{fmtCAD(est.total)}</div>
            </div>
          </div>

          <div className="px-5 sm:px-8 py-5 border-b border-slate-100">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Prepared for</div>
            <div className="text-sm font-medium text-slate-900 mt-1">{customer.name}</div>
            {customer.billing_address && <div className="text-xs text-slate-500">{customer.billing_address}</div>}
          </div>

          <div className="px-5 sm:px-8 py-5">
            {(est.options || []).length > 0 ? (
              <OptionsCustomerView
                est={est}
                onApproveOption={(optId) => {
                  // Pass option id when opening modal
                  setShowApprove({ optionId: optId });
                }}
                decided={decided}
              />
            ) : (
            <div className="overflow-x-auto -mx-5 sm:mx-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500">
                    <th className="px-5 sm:px-0 py-2 text-left font-medium">Description</th>
                    <th className="py-2 text-right font-medium w-16">Qty</th>
                    <th className="py-2 text-right font-medium w-24">Rate</th>
                    <th className="px-5 sm:px-0 py-2 text-right font-medium w-24">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(est.line_items || []).map((li) => {
                    const lineTotal = Number(li.quantity || 0) * Number(li.rate || 0);
                    return (
                      <tr key={li.id} className="border-b border-slate-100">
                        <td className="px-5 sm:px-0 py-3 align-top">
                          <div className="font-medium text-slate-900">{li.name}</div>
                          {li.description && <div className="text-xs text-slate-500">{li.description}</div>}
                        </td>
                        <td className="py-3 text-right tabular">{li.quantity}</td>
                        <td className="py-3 text-right tabular">{fmtCAD(li.rate)}</td>
                        <td className="px-5 sm:px-0 py-3 text-right tabular font-medium">{fmtCAD(lineTotal)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            )}
            {(est.options || []).length === 0 && (
            <div className="mt-5 flex justify-end">
              <div className="w-full sm:w-72 space-y-1.5 text-sm">
                <div className="flex justify-between text-slate-600"><span>Subtotal</span><span className="tabular">{fmtCAD(est.subtotal)}</span></div>
                <div className="flex justify-between text-slate-600"><span>HST ({est.hst_rate || 13}%)</span><span className="tabular">{fmtCAD(est.hst)}</span></div>
                <div className="flex justify-between font-bold text-slate-900 border-t border-slate-200 pt-2 mt-2 text-base"><span>Total</span><span className="tabular">{fmtCAD(est.total)}</span></div>
              </div>
            </div>
            )}
          </div>

          {est.notes && (
            <div className="px-5 sm:px-8 py-5 border-t border-slate-100 bg-slate-50/60">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Notes</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap mt-1">{est.notes}</p>
            </div>
          )}

          {/* Decision area */}
          <div className="px-5 sm:px-8 py-6 border-t border-slate-100">
            {decided ? (
              <div className={`p-4 rounded text-sm ${est.status === "Approved" ? "bg-emerald-50 border border-emerald-200 text-emerald-900" : "bg-rose-50 border border-rose-200 text-rose-900"}`}>
                <div className="font-bold">{est.status === "Approved" ? "✓ Approved" : "✗ Declined"}</div>
                {est.approval_name && <div className="text-xs mt-1">by {est.approval_name} · {fmtDate(est.approval_decided_at)}</div>}
                {est.selected_option_id && (est.options || []).length > 0 && (
                  <div className="text-xs mt-1">Selected: <b>{(est.options.find((o) => o.id === est.selected_option_id) || {}).name}</b></div>
                )}
                {est.decline_reason && <div className="text-xs mt-1">Reason: {est.decline_reason}</div>}
              </div>
            ) : (est.options || []).length === 0 ? (
              <div className="flex flex-col sm:flex-row gap-3">
                <button onClick={() => setShowApprove({})} data-testid="public-estimate-approve-btn" className="flex-1 px-5 py-3 text-white text-sm font-semibold rounded flex items-center justify-center gap-2 shadow-sm" style={{ background: "#10B981" }}>
                  <Check size={16}/> Approve estimate
                </button>
                <button onClick={() => setShowDecline(true)} data-testid="public-estimate-decline-btn" className="flex-1 px-5 py-3 border border-slate-300 text-slate-700 text-sm font-semibold rounded hover:bg-slate-50 flex items-center justify-center gap-2">
                  <X size={16}/> Decline
                </button>
              </div>
            ) : (
              <button onClick={() => setShowDecline(true)} data-testid="public-estimate-decline-all-btn" className="w-full px-5 py-3 border border-slate-300 text-slate-700 text-sm font-semibold rounded hover:bg-slate-50 flex items-center justify-center gap-2">
                <X size={16}/> Decline all options
              </button>
            )}
          </div>
        </div>

        <p className="text-center text-[11px] text-slate-400 mt-6 print:hidden">
          Questions about this estimate? Reply to the email you received.
        </p>
      </main>

      {showApprove && (
        <ApproveModal
          token={token} onClose={() => setShowApprove(null)}
          onDone={() => { setShowApprove(null); load(); }}
          primary={primary}
          selectedOptionId={showApprove.optionId || null}
          optionName={showApprove.optionId ? (est.options.find((o) => o.id === showApprove.optionId) || {}).name : null}
        />
      )}
      {showDecline && (
        <DeclineModal
          token={token} onClose={() => setShowDecline(false)}
          onDone={() => { setShowDecline(false); load(); }}
        />
      )}
    </div>
  );
}

function OptionsCustomerView({ est, onApproveOption, decided }) {
  const options = est.options || [];
  const labelTags = ["Good", "Better", "Best", "Option D", "Option E"];

  return (
    <div data-testid="options-customer-view" className="space-y-4">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        Choose the option that works for you
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {options.map((o, i) => {
          const isSelected = est.selected_option_id === o.id && est.status === "Approved";
          return (
            <div
              key={o.id}
              data-testid={`option-card-${i}`}
              className={`border rounded-lg overflow-hidden flex flex-col ${isSelected ? "border-emerald-500 ring-2 ring-emerald-300" : i === 1 && options.length === 3 ? "border-[#0F4C81] shadow-md" : "border-slate-200"}`}
            >
              <div className={`px-4 py-3 ${i === 1 && options.length === 3 ? "bg-[#0F4C81] text-white" : "bg-slate-50 text-slate-700"}`}>
                <div className="text-[10px] uppercase tracking-widest font-semibold opacity-80">{labelTags[i] || `Option ${i + 1}`}</div>
                <div className="font-display font-bold text-base mt-0.5">{o.name}</div>
                {i === 1 && options.length === 3 && (
                  <div className="text-[10px] mt-1 bg-white/20 inline-block px-1.5 py-0.5 rounded">Most popular</div>
                )}
              </div>
              {o.description && (
                <div className="px-4 py-2 text-xs text-slate-600 border-b border-slate-100 bg-white">{o.description}</div>
              )}
              <ul className="px-4 py-3 text-xs space-y-1.5 flex-1 bg-white">
                {(o.line_items || []).slice(0, 6).map((li) => (
                  <li key={li.id} className="flex items-start gap-1.5">
                    <Check size={11} className="text-emerald-600 mt-0.5 shrink-0"/>
                    <span className="text-slate-700 truncate">{li.name}{li.quantity > 1 ? ` ×${li.quantity}` : ""}</span>
                  </li>
                ))}
                {(o.line_items || []).length > 6 && (
                  <li className="text-slate-400 italic">+ {o.line_items.length - 6} more…</li>
                )}
              </ul>
              <div className="px-4 py-3 border-t border-slate-100 bg-slate-50">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">Total incl. HST</div>
                <div className="font-display text-2xl font-bold tabular text-slate-900">{fmtCAD(o.total)}</div>
              </div>
              {!decided && (
                <button
                  data-testid={`option-approve-${i}`}
                  onClick={() => onApproveOption(o.id)}
                  className={`px-4 py-3 text-sm font-semibold ${i === 1 && options.length === 3 ? "bg-[#0F4C81] hover:bg-[#0A365C] text-white" : "bg-emerald-500 hover:bg-emerald-600 text-white"}`}
                >
                  Choose {labelTags[i] || `Option ${i + 1}`}
                </button>
              )}
              {isSelected && (
                <div className="px-4 py-2 text-xs bg-emerald-50 text-emerald-800 font-semibold text-center">✓ You approved this option</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ApproveModal({ token, onClose, onDone, primary, selectedOptionId, optionName }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [legalAccepted, setLegalAccepted] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };
  const begin = (e) => { drawing.current = true; const c = canvasRef.current.getContext("2d"); const p = pos(e); c.beginPath(); c.moveTo(p.x, p.y); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const c = canvasRef.current.getContext("2d");
    const p = pos(e);
    c.lineTo(p.x, p.y); c.strokeStyle = "#0f172a"; c.lineWidth = 2; c.lineCap = "round"; c.stroke();
  };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current.getContext("2d"); c.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height); };

  const submit = async () => {
    setErr("");
    if (!name.trim()) { setErr("Please type your full name"); return; }
    const sig = canvasRef.current.toDataURL("image/png");
    if (sig.length < 1200) { setErr("Please draw your signature"); return; }
    if (!legalAccepted) { setErr("Please confirm the electronic signature consent below"); return; }
    setBusy(true);
    try {
      await publicApi.post(`/estimate/${token}/decision`, {
        decision: "approve", approval_name: name.trim(), approval_signature: sig,
        selected_option_id: selectedOptionId || undefined,
        legal_agreement_accepted: true,
      });
      onDone();
    } catch (e) { setErr(formatPublicErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4">
      <div data-testid="approve-modal" className="bg-white rounded-t-lg sm:rounded-lg w-full sm:max-w-md shadow-xl">
        <header className="px-5 py-4 border-b border-slate-100 flex justify-between items-center">
          <h3 className="font-display text-lg font-bold text-slate-900">
            {optionName ? `Approve "${optionName}"` : "Approve estimate"}
          </h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-900"><X size={18}/></button>
        </header>
        <div className="px-5 py-4 space-y-4">
          {err && <div className="text-sm text-red-700 bg-red-50 p-2 rounded">{err}</div>}
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Your full name</label>
            <input data-testid="approve-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Jane Smith" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:border-[#0F4C81]"/>
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Sign below</label>
              <button onClick={clear} type="button" className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1"><Eraser size={11}/> Clear</button>
            </div>
            <canvas
              ref={canvasRef} width={360} height={140}
              className="mt-1 w-full border border-dashed border-slate-300 rounded bg-slate-50 touch-none"
              onMouseDown={begin} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
              onTouchStart={begin} onTouchMove={move} onTouchEnd={end}
              data-testid="approve-signature-canvas"
            />
            <p className="text-[11px] text-slate-500 mt-1">By approving, you agree to the scope and pricing in this estimate.</p>
          </div>
          <label className="flex items-start gap-2 text-[11px] text-slate-700 cursor-pointer bg-slate-50 border border-slate-200 rounded p-2.5">
            <input
              type="checkbox"
              data-testid="approve-legal-consent"
              checked={legalAccepted}
              onChange={(e) => setLegalAccepted(e.target.checked)}
              className="mt-0.5 shrink-0"
            />
            <span>
              I agree that my electronic signature above is the legal equivalent of my handwritten signature
              under the Ontario <em>Electronic Commerce Act, 2000</em>. I confirm I have the authority to
              approve this estimate at the amount shown. A timestamped audit record (IP address and date/time)
              will be attached to this approval.
            </span>
          </label>
          <button onClick={submit} disabled={busy} data-testid="approve-submit" className="w-full px-5 py-3 text-white text-sm font-semibold rounded disabled:opacity-50" style={{ background: primary }}>
            {busy ? "Submitting…" : `Approve${optionName ? ` "${optionName}"` : ""} & submit signature`}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeclineModal({ token, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await publicApi.post(`/estimate/${token}/decision`, { decision: "decline", decline_reason: reason });
      onDone();
    } catch (e) { alert(formatPublicErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4">
      <div data-testid="decline-modal" className="bg-white rounded-t-lg sm:rounded-lg w-full sm:max-w-md shadow-xl">
        <header className="px-5 py-4 border-b border-slate-100 flex justify-between items-center">
          <h3 className="font-display text-lg font-bold">Decline estimate</h3>
          <button onClick={onClose} className="p-1 text-slate-400 hover:text-slate-900"><X size={18}/></button>
        </header>
        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">Reason (optional)</label>
            <textarea data-testid="decline-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={5} placeholder="Help us understand…" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none"/>
          </div>
          <button onClick={submit} disabled={busy} data-testid="decline-submit" className="w-full px-5 py-3 bg-rose-600 text-white text-sm font-semibold rounded disabled:opacity-50">
            {busy ? "Submitting…" : "Decline estimate"}
          </button>
        </div>
      </div>
    </div>
  );
}
